import {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import apiService from '../services/apiService';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useOptionalData } from '../contexts/DataContext';
import useAppChrome from './useAppChrome';
import useQuickGuard from './useQuickGuard';
import useUndoToast from './useUndoToast';
import { loadDirectory } from '../components/shared/UserSearch';
import {
    boardSubtitle,
    boardTasks,
    branchCount,
    GROUP_OPTIONS,
    groupTasks,
    hasActiveFilters,
    indexUnitsByStock,
    personName,
    QUICK_VIEWS,
    undoToastText,
    unitForTask,
} from '../utils/taskBoard';

// The "group by" choice is remembered per browser.
export const GROUP_BY_STORAGE_KEY = 'ironsight.taskBoard.groupBy';
// "Show closed" is on by default; switching it off is remembered per browser.
export const SHOW_CLOSED_STORAGE_KEY = 'ironsight.taskBoard.showClosed';

export const readGroupBy = () => {
    try {
        const stored = window.localStorage.getItem(GROUP_BY_STORAGE_KEY);
        return GROUP_OPTIONS.some((g) => g.id === stored) ? stored : 'status';
    } catch {
        return 'status';
    }
};

const writeGroupBy = (value) => {
    try {
        window.localStorage.setItem(GROUP_BY_STORAGE_KEY, value);
    } catch {
        // Storage blocked (private window, policy): the choice lasts this visit.
    }
};

export const readShowClosed = () => {
    try {
        return window.localStorage.getItem(SHOW_CLOSED_STORAGE_KEY) !== '0';
    } catch {
        return true;
    }
};

const writeShowClosed = (value) => {
    try {
        window.localStorage.setItem(SHOW_CLOSED_STORAGE_KEY, value ? '1' : '0');
    } catch {
        // Storage blocked: the choice lasts this visit.
    }
};

// Cards and columns are found by data attributes, so today's board and the
// redesigned one share the focus and arrow-key logic.
const CARD_SEL = '[data-task-id]';
const COLUMN_SEL = '[data-col-key]';

/**
 * The Task Board's data, state and behaviour, shared by today's page
 * (TaskBoard/TaskBoard) and the redesigned one (TasksV2) so they cannot
 * drift: the two loads (activities + users), the joined inventory, the
 * directory names, search / type / quick view / assignee, group by and Show
 * closed (both remembered per browser), the columns, optimistic status
 * changes with the Undo toast, edit and delete, the 400 ms quick guard,
 * focus that follows a card that leaves, arrow-key moves and drag and drop.
 *
 * `restore`: the view to start from (Back from a unit page, D7).
 */
export default function useTaskBoard({ orgName, restore = null } = {}) {
    // Read-only for SAG staff looking at a customer's board — the backend
    // refuses the writes, so the board does not offer them.
    const { readOnly } = useWorkspace();
    const canEdit = !readOnly;
    // The dealership in view, shared with the v2 top bar (hooks/useAppChrome).
    const { dealership } = useAppChrome({ orgName });

    // Unit details on the cards come from the inventory the app already holds
    // (shared with the dashboard); a task whose unit is gone renders without.
    const data = useOptionalData();
    const loadInventory = data?.loadInventory;
    const inventoryUnits = data?.inventoryData?.units;
    useEffect(() => { if (loadInventory) loadInventory(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const unitsByStock = useMemo(() => indexUnitsByStock(inventoryUnits), [inventoryUnits]);
    // Inventory is still on its way (it can take a while on a cold cache):
    // cards show without unit details, and nothing is judged "missing" yet.
    const unitsLoading = Boolean(data) && !data.inventoryData && !data.inventoryError;
    const inventoryLoaded = Boolean(data?.inventoryData);
    const unitFor = useCallback((task) => unitForTask(task, unitsByStock), [unitsByStock]);
    const unitStatusFor = (task, unit = unitFor(task)) => {
        if (unit) return 'found';
        return inventoryLoaded ? 'missing' : 'unknown';
    };
    /** A unit that is not in the loaded inventory has no page to open. */
    const canOpenStock = (stockNumber) => !(inventoryLoaded && !unitsByStock.get(String(stockNumber).trim()));

    // Display names come from the directory the assignee picker already loads.
    const [directory, setDirectory] = useState(() => new Map());
    useEffect(() => {
        let alive = true;
        loadDirectory().then((list) => {
            if (!alive) return;
            const map = new Map();
            (list || []).forEach((u) => {
                const email = typeof u === 'string' ? u : u?.email;
                if (email && u?.name) map.set(String(email).toLowerCase(), u.name);
            });
            setDirectory(map);
        });
        return () => { alive = false; };
    }, []);
    const nameFor = useCallback((email) => personName(email, directory), [directory]);

    const [activities, setActivities] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    // Today's board shows an empty board when a load fails; the redesigned
    // one shows this as an error with Retry.
    const [error, setError] = useState(false);
    const [assignee, setAssignee] = useState(restore?.assignee || '');
    const [typeFilter, setTypeFilter] = useState(restore?.typeFilter || 'all');
    const [search, setSearch] = useState(restore?.search || '');
    const [quickView, setQuickView] = useState(restore?.quickView || null);
    const [groupBy, setGroupBy] = useState(() => restore?.groupBy || readGroupBy());
    const [showClosed, setShowClosed] = useState(() => (restore && typeof restore.showClosed === 'boolean' ? restore.showClosed : readShowClosed()));
    const [pendingFocus, setPendingFocus] = useState(null);
    const [dragItem, setDragItem] = useState(null);
    const [dropTarget, setDropTarget] = useState(null);
    const boardRef = useRef(null);
    const quickGuard = useQuickGuard();
    const undoToast = useUndoToast();

    const fetchAll = useCallback((showLoading = false) => {
        if (showLoading) setLoading(true);
        return Promise.all([
            apiService.getActivities(),
            apiService.getActivityUsers(),
        ]).then(([actData, userData]) => {
            setActivities(actData.activities || []);
            setUsers(userData.users || []);
            setError(false);
        }).catch(() => {
            setActivities([]);
            setError(true);
        }).finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchAll(true); }, [fetchAll]);

    const retry = useCallback(() => fetchAll(true), [fetchAll]);

    // If focus is on (or inside) this task's card, note where it should go if
    // the card leaves the board: the card itself if still shown, else the next
    // card in its column, the previous one, the column, the board.
    const rememberFocus = (taskId) => {
        const board = boardRef.current;
        const active = document.activeElement;
        if (!board || !active || !board.contains(active)) return;
        const card = active.closest(CARD_SEL);
        if (!card || card.dataset.taskId !== String(taskId)) return;
        const column = card.closest(COLUMN_SEL);
        const cards = Array.from(column?.querySelectorAll(CARD_SEL) || []);
        const i = cards.indexOf(card);
        setPendingFocus({
            taskId: String(taskId),
            neighbours: [cards[i + 1], cards[i - 1]].filter(Boolean).map((c) => c.dataset.taskId),
            columnKey: column?.dataset.colKey,
        });
    };

    // Runs once per request and always clears it, so a hidden card can never
    // grab focus later.
    useEffect(() => {
        if (!pendingFocus) return;
        const board = boardRef.current;
        const cards = Array.from(board?.querySelectorAll(CARD_SEL) || []);
        const byId = (id) => cards.find((c) => c.dataset.taskId === id);
        const column = Array.from(board?.querySelectorAll(COLUMN_SEL) || [])
            .find((c) => c.dataset.colKey === pendingFocus.columnKey);
        const target = byId(pendingFocus.taskId)
            || pendingFocus.neighbours.map(byId).find(Boolean)
            || column
            || board;
        target?.focus();
        setPendingFocus(null);
    }, [pendingFocus]);

    const chooseShowClosed = (value) => {
        setShowClosed(value);
        writeShowClosed(value);
    };

    const chooseGroupBy = (value) => {
        setGroupBy(value);
        writeGroupBy(value);
    };

    const statusChangeRef = useRef(null);
    const handleStatusChange = async (activityId, newStatus, { silent = false } = {}) => {
        // Completing or cancelling (by button, key, confirmation or drag)
        // offers an Undo that puts the previous status back.
        const prevStatus = activities.find((a) => a.id === activityId)?.status;
        const text = silent ? null : undoToastText(prevStatus, newStatus);
        if (text) {
            undoToast.offer({
                text,
                onUndo: () => statusChangeRef.current(activityId, prevStatus, { silent: true }),
            });
        }
        rememberFocus(activityId);
        // Optimistic update — move card instantly. Stamp the close locally
        // too, so the card stays inside the "last 30 days" window until the
        // refetch brings the server's stamp.
        const stamp = new Date().toISOString();
        setActivities((prev) => prev.map((a) => (a.id === activityId ? {
            ...a,
            status: newStatus,
            updated_at: stamp,
            completed_at: newStatus === 'completed' ? stamp : null,
        } : a)));
        try {
            await apiService.updateActivityStatus(activityId, { status: newStatus });
            fetchAll();
        } catch (err) {
            console.error('Failed to update', err); // eslint-disable-line no-console
            fetchAll();
        }
    };
    statusChangeRef.current = handleStatusChange;

    const handleEdit = async (activityId, updates) => {
        setActivities((prev) => prev.map((a) => (a.id === activityId ? { ...a, ...updates } : a)));
        try {
            await apiService.updateActivity(activityId, updates);
        } catch (err) {
            console.error('Failed to edit', err); // eslint-disable-line no-console
        }
        fetchAll();
    };

    // Start / Complete / Reopen from the hover buttons. The first click moves
    // the card, so the second click of a double-click would hit the next
    // card's button: ignore it, and anything within the guard window.
    const handleQuickStatus = (activityId, newStatus, e) => {
        if (!quickGuard(e)) return;
        handleStatusChange(activityId, newStatus);
    };

    const handleDelete = async (activityId) => {
        rememberFocus(activityId);
        setActivities((prev) => prev.filter((a) => a.id !== activityId));
        try {
            await apiService.deleteActivity(activityId);
        } catch (err) {
            console.error('Failed to delete', err); // eslint-disable-line no-console
            fetchAll();
        }
    };

    // Arrow keys: up/down within a column, left/right to the nearest column
    // that has cards, keeping the row where possible.
    const handleNavigate = (taskId, key) => {
        const cols = Array.from(boardRef.current?.querySelectorAll(COLUMN_SEL) || [])
            .map((col) => Array.from(col.querySelectorAll(CARD_SEL)));
        const ci = cols.findIndex((cards) => cards.some((c) => c.dataset.taskId === String(taskId)));
        if (ci === -1) return;
        const ri = cols[ci].findIndex((c) => c.dataset.taskId === String(taskId));
        let target = null;
        if (key === 'ArrowUp') target = cols[ci][ri - 1];
        else if (key === 'ArrowDown') target = cols[ci][ri + 1];
        else {
            const step = key === 'ArrowLeft' ? -1 : 1;
            for (let i = ci + step; i >= 0 && i < cols.length; i += step) {
                if (cols[i].length) {
                    target = cols[i][Math.min(ri, cols[i].length - 1)];
                    break;
                }
            }
        }
        target?.focus();
    };

    // T-9 (changed by the user 2026-10-07): a drop on Cancelled asks first,
    // like "Cancel task" in the card menu; every other drop moves at once.
    const [pendingCancel, setPendingCancel] = useState(null); // the task to cancel
    const handleDrop = (columnStatus) => {
        setDropTarget(null);
        if (!canEdit) return;
        if (dragItem && dragItem.status !== columnStatus) {
            if (columnStatus === 'cancelled') setPendingCancel(dragItem);
            else handleStatusChange(dragItem.id, columnStatus);
        }
        setDragItem(null);
    };
    const confirmPendingCancel = () => {
        const task = pendingCancel;
        setPendingCancel(null);
        if (task) handleStatusChange(task.id, 'cancelled');
    };
    const keepPendingCancel = () => setPendingCancel(null);
    const endDrag = () => { setDragItem(null); setDropTarget(null); };

    const toggleQuickView = (id) => setQuickView((v) => (v === id ? null : id));

    const clearFilters = () => {
        setSearch('');
        setTypeFilter('all');
        setQuickView(null);
        setAssignee('');
    };
    /** Clear one filter (a pill's ×). */
    const clearFilter = (key) => {
        if (key === 'search') setSearch('');
        else if (key === 'typeFilter') setTypeFilter('all');
        else if (key === 'quickView') setQuickView(null);
        else if (key === 'assignee') setAssignee('');
    };

    const now = new Date();
    const view = {
        unitsByStock, quickView, assignee, typeFilter, search, showClosed,
    };
    const { filtered, hiddenClosed } = boardTasks(activities, view, now);
    const columns = groupTasks(filtered, groupBy, unitsByStock, { showClosed, nameFor });
    const quickCounts = Object.fromEntries(QUICK_VIEWS.map((q) => [q.id, activities.filter((a) => q.test(a, now)).length]));
    const branches = branchCount(inventoryUnits);

    return {
        // who and where
        canEdit,
        readOnly: Boolean(readOnly),
        dealership,
        subtitle: boardSubtitle(dealership, branches),
        branches,
        // data
        activities,
        users,
        loading,
        error,
        retry,
        data,
        inventoryUnits,
        unitsByStock,
        unitsLoading,
        inventoryLoaded,
        unitFor,
        unitStatusFor,
        canOpenStock,
        nameFor,
        // filters and view
        view,
        search,
        setSearch,
        typeFilter,
        setTypeFilter,
        assignee,
        setAssignee,
        quickView,
        setQuickView,
        toggleQuickView,
        groupBy,
        chooseGroupBy,
        showClosed,
        chooseShowClosed,
        clearFilters,
        clearFilter,
        filtersActive: hasActiveFilters(view),
        filtered,
        hiddenClosed,
        columns,
        quickCounts,
        now,
        // board
        boardRef,
        dragEnabled: canEdit && groupBy === 'status',
        dragItem,
        setDragItem,
        dropTarget,
        setDropTarget,
        handleDrop,
        endDrag,
        pendingCancel,
        confirmPendingCancel,
        keepPendingCancel,
        handleStatusChange,
        handleQuickStatus,
        handleEdit,
        handleDelete,
        handleNavigate,
        // the Undo toast
        toast: undoToast.toast,
        undo: undoToast.undo,
        dismissToast: undoToast.dismiss,
    };
}
